/**
 * FireGuard Frontend Entry Point
 * Initializes the SPA and configures routing
 */

import './components/LoginPage.js';
import './components/RegisterPage.js';
import './components/DashboardPage.js';
import './components/ScanPage.js';
import './components/ExtinguisherListPage.js';
import './components/ExtinguisherDetailPage.js';
import './components/InspectionFormPage.js';
import './components/LocationListPage.js';
import './components/LocationDetailPage.js';
import './components/ReportsPage.js';
import './components/SettingsPage.js';
import './components/PrivacyPolicyPage.js';
import './components/TermsOfServicePage.js';
import './components/TeamPage.js';
import './components/NotificationSettingsPage.js';
import './components/auth/ForgotPasswordPage.js';
import './components/auth/ResetPasswordPage.js';
import './components/auth/AcceptInvitationPage.js';
import './components/BillingPage.js';
import './components/shared/Modal.js';
import './components/shared/Toast.js';
import './components/shared/LoadingSpinner.js';
import './components/shared/OfflineIndicator.js';
import './components/shared/InstallPrompt.js';
import { router } from './lib/router.js';
import { api } from './lib/api.js';
import { clearExpiredCache } from './lib/offline-storage.js';

console.log('FireGuard initializing...');

// Get the app container
const app = document.getElementById('app');

if (!app) {
  throw new Error('App container not found');
}

/**
 * Render a page component
 */
const PAGE_TITLES: Record<string, string> = {
  'login-page': 'Sign In',
  'register-page': 'Start Your 45-Day Free Trial',
  'dashboard-page': 'Dashboard',
  'scan-page': 'Scan QR Code',
  'extinguisher-list-page': 'Extinguishers',
  'extinguisher-detail-page': 'Extinguisher',
  'inspection-form-page': 'Monthly Inspection',
  'location-list-page': 'Locations',
  'location-detail-page': 'Location',
  'reports-page': 'Reports',
  'settings-page': 'Settings',
  'team-page': 'Team',
  'notification-settings-page': 'Notification Settings',
  'billing-page': 'Plan & Billing',
  'forgot-password-page': 'Reset Password',
  'reset-password-page': 'Choose a New Password',
  'accept-invitation-page': 'Join Your Team',
  'privacy-policy-page': 'Privacy Policy',
  'terms-of-service-page': 'Terms of Service',
};

function renderPage(componentName: string): void {
  document.title = `${PAGE_TITLES[componentName] || 'FireGuard'} | FireGuard`;
  app.innerHTML = `<${componentName}></${componentName}>`;
}

/**
 * Configure routes
 */
function configureRoutes(): void {
  // Public routes
  router.on('/', () => {
    // Redirect to login or dashboard based on auth
    if (api.isAuthenticated()) {
      router.replace('/dashboard');
    } else {
      router.replace('/login');
    }
  });

  router.on('/login', () => {
    // Redirect to dashboard if already authenticated
    if (api.isAuthenticated()) {
      router.replace('/dashboard');
      return;
    }
    renderPage('login-page');
  });

  router.on('/register', () => {
    // Redirect to dashboard if already authenticated
    if (api.isAuthenticated()) {
      router.replace('/dashboard');
      return;
    }
    renderPage('register-page');
  });

  router.on('/forgot-password', () => renderPage('forgot-password-page'));
  router.on('/reset-password', () => renderPage('reset-password-page'));
  router.on('/accept-invitation', () => {
    // Invited users may already hold a stale token from another account; clear it.
    api.clearToken();
    renderPage('accept-invitation-page');
  });

  // Legal pages (public)
  router.on('/privacy-policy', () => {
    renderPage('privacy-policy-page');
  });

  router.on('/terms-of-service', () => {
    renderPage('terms-of-service-page');
  });

  // Protected routes (require authentication)
  router.on('/dashboard', () => {
    renderPage('dashboard-page');
  }, true);

  router.on('/scan', () => {
    renderPage('scan-page');
  }, true);

  router.on('/extinguishers', () => {
    renderPage('extinguisher-list-page');
  }, true);

  router.on('/extinguishers/:id', () => {
    renderPage('extinguisher-detail-page');
  }, true);

  router.on('/extinguishers/:id/inspect', () => {
    renderPage('inspection-form-page');
  }, true);

  router.on('/locations', () => {
    renderPage('location-list-page');
  }, true);

  router.on('/locations/:id', () => {
    renderPage('location-detail-page');
  }, true);

  router.on('/reports', () => {
    renderPage('reports-page');
  }, true);

  router.on('/settings', () => {
    renderPage('settings-page');
  }, true);

  router.on('/team', () => {
    renderPage('team-page');
  }, true);

  router.on('/notifications', () => {
    renderPage('notification-settings-page');
  }, true);

  router.on('/billing', () => {
    renderPage('billing-page');
  }, true);

  // 404 handler
  router.notFound(() => {
    app.innerHTML = `
      <div style="text-align: center; padding: 40px; font-family: sans-serif;">
        <h1 style="font-size: 48px; color: #ef4444;">404</h1>
        <p style="color: #6b7280; margin-top: 16px;">Page not found</p>
        <a href="/dashboard" style="color: #3b82f6; text-decoration: none; margin-top: 16px; display: inline-block;">
          Go to Dashboard
        </a>
      </div>
    `;
  });
}

/**
 * Initialize the application
 */
function initApp(): void {
  console.log('Configuring routes...');
  configureRoutes();

  console.log('Starting router...');
  router.start();

  // Add offline indicator
  const offlineIndicator = document.createElement('offline-indicator');
  document.body.appendChild(offlineIndicator);

  // Add install prompt
  const installPrompt = document.createElement('install-prompt');
  document.body.appendChild(installPrompt);

  // Clear expired cache on startup
  clearExpiredCache().catch((error) => {
    console.error('Failed to clear expired cache:', error);
  });

  // Clear expired cache periodically (every hour)
  setInterval(() => {
    clearExpiredCache().catch((error) => {
      console.error('Failed to clear expired cache:', error);
    });
  }, 3600000); // 1 hour

  console.log('FireGuard ready!');
}

// Service Worker Registration (PWA)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log('ServiceWorker registered:', registration.scope);
      })
      .catch((error) => {
        console.log('ServiceWorker registration failed (normal in dev):', error);
      });
  });
}

// Start the app when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}
